import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, TrendingUp, Clock, DollarSign, Activity, ExternalLink, Plus, Bot } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { marketsApi, opportunitiesApi } from '../lib/api'
import { formatCurrency, formatDate } from '../lib/utils'
import { TradeModal } from '../components/TradeModal'

export function MarketDetail() {
  const { id } = useParams()
  const queryClient = useQueryClient()
  const [showTradeModal, setShowTradeModal] = useState(false)
  const [selectedOpportunity, setSelectedOpportunity] = useState(null)

  const { data: market, isLoading } = useQuery({
    queryKey: ['market', id],
    queryFn: () => marketsApi.getById(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: opportunities } = useQuery({
    queryKey: ['opportunities', id],
    queryFn: () => opportunitiesApi.getAll().then(r => 
      r.data.filter((o: any) => o.marketId === id)
    ),
    enabled: !!id,
  })

  const trackMutation = useMutation({
    mutationFn: () => marketsApi.track(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['market', id] }),
  })

  const handleTrade = (opportunity?: any) => {
    setSelectedOpportunity(opportunity)
    setShowTradeModal(true)
  }

  if (isLoading) {
    return <div className="text-center py-12">Loading market...</div>
  }

  if (!market) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Market not found</p>
        <Link to="/markets" className="text-blue-600 hover:underline mt-4 inline-block">
          Back to Markets
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Link to="/markets" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-2">
            {market.category}
          </span>
          <h1 className="text-2xl font-bold text-gray-900">{market.question}</h1>
        </div>
        <div className="flex items-center space-x-2">
          {!market.isTracked ? (
            <button
              onClick={() => trackMutation.mutate()}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Track
            </button>
          ) : (
            <span className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-green-100 text-green-800">
              Tracked
            </span>
          )}
          <button
            onClick={() => handleTrade()}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Trade
          </button>
        </div>
      </div>

      {/* Price Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 font-medium">YES Price</p>
              <p className="text-4xl font-bold text-green-600">{(market.yesPrice * 100).toFixed(1)}¢</p>
            </div>
            <TrendingUp className="w-12 h-12 text-green-600" />
          </div>
          <p className="mt-2 text-sm text-green-600">
            ${formatCurrency(market.yesPrice)} per share
          </p>
        </div>

        <div className="bg-red-50 rounded-xl p-6 border-2 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 font-medium">NO Price</p>
              <p className="text-4xl font-bold text-red-600">{(market.noPrice * 100).toFixed(1)}¢</p>
            </div>
            <TrendingUp className="w-12 h-12 text-red-600" />
          </div>
          <p className="mt-2 text-sm text-red-600">
            ${formatCurrency(market.noPrice)} per share
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Volume</p>
              <p className="text-2xl font-bold">{formatCurrency(market.volume || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <Activity className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Liquidity</p>
              <p className="text-2xl font-bold">{formatCurrency(market.liquidity || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <Clock className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">Ends</p>
              <p className="text-lg font-bold">{formatDate(market.endDate)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center">
            <Bot className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm text-gray-500">AI Signals</p>
              <p className="text-2xl font-bold">{opportunities?.length || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Opportunities */}
      {opportunities && opportunities.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">AI Detected Opportunities</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {opportunities.map((opp: any) => (
              <div key={opp.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {opp.type}
                      </span>
                      <span className="text-sm text-gray-500">
                        Edge: +{(opp.edge * 100).toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-gray-700">{opp.description}</p>
                    <div className="mt-2 flex items-center space-x-4 text-sm">
                      <span className="text-gray-500">
                        Confidence: {(opp.confidence * 100).toFixed(0)}%
                      </span>
                      <span className="text-gray-500">
                        Expected Value: +{(opp.expectedValue * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleTrade(opp)}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Trade
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* External Links */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">External Links</h2>
        <div className="flex flex-wrap gap-3">
          {market.slug && (
            <a
              href={`https://polymarket.com/event/${market.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              View on Polymarket
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>
          )}
          {market.conditionId && (
            <a
              href={`https://polygonscan.com/address/${market.conditionId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              View on PolygonScan
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>
          )}
        </div>
      </div>

      {/* Trade Modal */}
      {showTradeModal && (
        <TradeModal
          isOpen={showTradeModal}
          onClose={() => {
            setShowTradeModal(false)
            setSelectedOpportunity(null)
          }}
          marketId={market.id}
          marketName={market.question}
          currentPrice={market.yesPrice}
          yesTokenId={market.yesTokenId}
          noTokenId={market.noTokenId}
        />
      )}
    </div>
  )
}
